import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tag, Trash, PencilSimple, Plus } from "@phosphor-icons/react";

export default function EspecificacionesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nuevo, setNuevo] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // {id, nombre}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/especificaciones");
      setItems(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cargar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const crear = async (e) => {
    e?.preventDefault?.();
    const nombre = nuevo.trim();
    if (!nombre) return;
    setCreating(true);
    try {
      await api.post("/especificaciones", { nombre });
      toast.success(`"${nombre}" creada`);
      setNuevo("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear");
    } finally {
      setCreating(false);
    }
  };

  const guardarEdit = async () => {
    if (!editing) return;
    const nombre = editing.nombre.trim();
    if (!nombre) return;
    try {
      await api.put(`/especificaciones/${editing.id}`, { nombre, orden: editing.orden || 0 });
      toast.success("Actualizada");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar");
    }
  };

  const borrar = async (it) => {
    if (!confirm(`¿Eliminar la especificación "${it.nombre}"?`)) return;
    try {
      await api.delete(`/especificaciones/${it.id}`);
      toast.success("Eliminada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-6" data-testid="especificaciones-page">
      <div className="flex items-center gap-3 border-b border-neutral-200 pb-3">
        <Tag size={22} weight="fill" className="text-amber-600" />
        <div>
          <h1 className="text-xl font-mono uppercase tracking-widest">Especificaciones de producto</h1>
          <p className="text-xs text-neutral-500 mt-0.5">
            Modificadores que se aplican a un ítem de pedido (ej: Interfoleado, Churrasco, Entero).
            Aparecen como opción al cargar cada línea de un pedido y agrupan los ítems en la orden de producción.
          </p>
        </div>
      </div>

      {/* Crear nueva */}
      <form onSubmit={crear} className="flex gap-2 items-center bg-neutral-50 border border-neutral-200 rounded px-3 py-2">
        <Input
          value={nuevo}
          onChange={(e) => setNuevo(e.target.value)}
          placeholder="Nombre de la nueva especificación…"
          className="flex-1 h-8 text-sm"
          data-testid="espec-new-input"
        />
        <Button
          type="submit"
          disabled={creating || !nuevo.trim()}
          size="sm"
          className="bg-amber-600 hover:bg-amber-700"
          data-testid="espec-new-btn"
        >
          <Plus size={14} weight="bold" className="mr-1" />
          Crear
        </Button>
      </form>

      {/* Listado */}
      <div className="border border-neutral-200 rounded overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-neutral-50 text-[10px] uppercase font-mono text-neutral-500 border-b">
          <div className="col-span-9">Nombre</div>
          <div className="col-span-3 text-right">Acciones</div>
        </div>
        {loading ? (
          <div className="p-4 text-center text-neutral-400 text-sm">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-4 text-center text-neutral-400 text-sm italic">
            No hay especificaciones. Creá la primera arriba.
          </div>
        ) : items.map((it) => (
          <div key={it.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-neutral-100 items-center hover:bg-amber-50/40" data-testid={`espec-row-${it.nombre}`}>
            <div className="col-span-9">
              {editing?.id === it.id ? (
                <Input
                  autoFocus
                  value={editing.nombre}
                  onChange={(e) => setEditing({ ...editing, nombre: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") guardarEdit();
                    if (e.key === "Escape") setEditing(null);
                  }}
                  className="h-7 text-sm"
                  data-testid={`espec-edit-input-${it.nombre}`}
                />
              ) : (
                <span className="font-mono text-sm text-amber-900">{it.nombre}</span>
              )}
            </div>
            <div className="col-span-3 flex justify-end gap-1">
              {editing?.id === it.id ? (
                <>
                  <Button size="sm" onClick={guardarEdit} className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-xs">Guardar</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="h-7 px-2 text-xs">Cancelar</Button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setEditing({ id: it.id, nombre: it.nombre, orden: it.orden || 0 })}
                    className="p-1.5 hover:bg-neutral-100 rounded text-neutral-600 hover:text-amber-700"
                    title="Editar"
                    data-testid={`espec-edit-btn-${it.nombre}`}
                  >
                    <PencilSimple size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => borrar(it)}
                    className="p-1.5 hover:bg-red-50 rounded text-neutral-600 hover:text-red-700"
                    title="Eliminar"
                    data-testid={`espec-del-btn-${it.nombre}`}
                  >
                    <Trash size={14} />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
